import { Activity, Fragment, useCallback, useMemo, useState } from "react";
import { useSetAtom } from "jotai";
import {
  Button,
  Division,
  DropZone,
  Flex,
  Heading,
  Icon,
  Icons,
  Form,
  Menu,
  type MenuItemProps,
  Modal,
  Paragraph,
  Select,
  Textarea,
  useToast,
} from "@ir/ui";
import classNames from "classnames";
import { DocumentMimeType } from "@/core/enums/DocumentMimeType";
import { WorkspacePermissions } from "@/core/permissions";
import { useUserPermissions } from "@/hooks";
import { HasAnyPermission } from "@/presentation/workspace";
import { submitPromotionDocumentAtom } from "@/store/promotion";
import { useProgramDetailsOptions } from "./edit/hooks/useProgramDetailsOptions";
import { CreateProgramDrawer } from "./create/CreateProgramDrawer";

const FILE_CONSTANTS = {
  MAX_SIZE_MB: 10,
  MAX_FILE_NAME_LENGTH: 20,
  ACCEPTED_TYPES: [
    DocumentMimeType.PDF,
    DocumentMimeType.EXCEL,
    DocumentMimeType.EXCEL_LEGACY,
    DocumentMimeType.EXCEL_LEGACY_2003_TEMPLATE,
    DocumentMimeType.EXCEL_LEGACY_2003_BINARY,
    DocumentMimeType.EXCEL_LEGACY_2003_MACROENABLED,
    DocumentMimeType.CSV,
    DocumentMimeType.JPEG,
    DocumentMimeType.PNG,
    DocumentMimeType.GIF,
    DocumentMimeType.TIFF,
    DocumentMimeType.BMP,
  ],
  ACCEPTED_EXTENSIONS: ["pdf", "xlsx", "xls", "xlsm", "csv", "jpg", "jpeg", "png", "gif", "tiff", "bmp"],
};

interface SelectedFile {
  file: File;
  error: string | null;
}

const getFileType = (file: File): DocumentMimeType => {
  return file.type.toLowerCase() as DocumentMimeType;
};

const sanitizeFileName = (filename: string): string => {
  const sanitized = filename
    .replaceAll(/[<>:"/\\|?*]/g, "")
    .replaceAll(/\s+/g, "_")
    .replaceAll(/[^a-zA-Z0-9._-]/g, "")
    .replaceAll(/^\.+/g, "")
    .replaceAll(/\.{2,}/g, ".")
    .trim();

  return sanitized || "untitled";
};

const getFileSizeInMb = (file: File): number => file.size / 1024 / 1024;

const getFileSizeInString = (file: File): string => {
  const sizeInMB = getFileSizeInMb(file);
  return sizeInMB < 1 ? `${(file.size / 1024).toFixed(2)} KB` : `${sizeInMB.toFixed(2)} MB`;
};

const isAcceptedExtension = (fileName: string): boolean => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  return extension ? FILE_CONSTANTS.ACCEPTED_EXTENSIONS.includes(extension) : false;
};

const validateFile = (file: File): { error: string | null; isValid: boolean } => {
  const fileSize = getFileSizeInMb(file);

  if (fileSize > FILE_CONSTANTS.MAX_SIZE_MB) {
    return { error: `File size must be less than ${FILE_CONSTANTS.MAX_SIZE_MB}MB`, isValid: false };
  }

  const fileType = getFileType(file);
  if (!FILE_CONSTANTS.ACCEPTED_TYPES.includes(fileType) && !isAcceptedExtension(file.name)) {
    return { error: "File type is not supported", isValid: false };
  }

  return { error: null, isValid: true };
};

const ModalHeader = () => (
  <Flex direction="column">
    <Heading className="font-bold display-sm text-primary-900">New Promotion</Heading>
    <Paragraph className="font-normal text-grey-500 m-0">
      Upload a promotional document to send for processing.
    </Paragraph>
  </Flex>
);

interface ModalFooterProps {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
}

const ModalFooter = ({ onClose, onSubmit, isSubmitting, canSubmit }: ModalFooterProps) => (
  <Flex gap={0.75} className="w-100">
    <Button variant="outline" color="grey" className="flex-1" onPress={onClose} isDisabled={isSubmitting}>
      Cancel
    </Button>
    <Button
      variant="solid"
      color="primary"
      className="flex-1"
      onPress={onSubmit}
      isDisabled={!canSubmit || isSubmitting}
      isLoading={isSubmitting}
    >
      Send for Processing
    </Button>
  </Flex>
);

interface NewPromotionUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewPromotionUploadModal = ({ isOpen, onClose }: NewPromotionUploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitPromotionDocument = useSetAtom(submitPromotionDocumentAtom);
  const toast = useToast();
  const [form] = Form.useForm();
  const purchaserIdValue = Form.useWatch("Purchaser", form);
  const { purchaserOptions, isPurchasersLoading } = useProgramDetailsOptions();

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setIsSubmitting(false);
    form.resetFields();
  }, [form]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }

    const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: getFileType(file) });
    const { error, isValid } = validateFile(sanitizedFile);

    setSelectedFile({
      file: sanitizedFile,
      error: isValid ? null : error,
    });
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const canSubmit = selectedFile !== null && selectedFile.error === null && purchaserIdValue != null;

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || selectedFile.error) {
      return;
    }

    setIsSubmitting(true);
    try {
      const values = await form.validateFields();

      const purchaserId = Number.parseInt(values.Purchaser.value, 10);
      const notes = values.Notes?.trim();

      console.log(purchaserId, notes);

      await submitPromotionDocument({
        file: selectedFile.file,
        purchaserId,
        notes: notes || undefined,
      });
      toast.success({ content: "Promotion document sent for processing" });
      handleClose();
    } catch (error) {
      toast.error({
        content: error instanceof Error ? error.message : "Failed to send promotion document for processing",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedFile, form, submitPromotionDocument, toast, handleClose]);

  const renderSelectedFile = () => {
    if (!selectedFile) {
      return null;
    }

    const fileName = selectedFile.file.name;
    const extension = fileName.split(".").pop();
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf("."));
    const displayName =
      nameWithoutExtension.length > FILE_CONSTANTS.MAX_FILE_NAME_LENGTH
        ? `${nameWithoutExtension.substring(0, FILE_CONSTANTS.MAX_FILE_NAME_LENGTH)}...${extension}`
        : fileName;

    return (
      <Flex
        direction="row"
        gap={1}
        align="center"
        className={classNames("p-2 border-1 border-color-grey-50 mt-2", {
          "border-color-error-500": selectedFile.error,
          "border-color-success-500": !selectedFile.error,
        })}
        style={{ borderRadius: 8 }}
      >
        <Icon name={Icons.File} color={selectedFile.error ? "error" : "grey"} />
        <Flex className="flex-1" direction="column">
          <Paragraph className="m-0 paragraph text-grey-700 text-md" style={{ whiteSpace: "nowrap" }} title={fileName}>
            {displayName}
          </Paragraph>
          {selectedFile.error && <Paragraph className="text-xs text-error-500 m-0">{selectedFile.error}</Paragraph>}
          <Paragraph className="text-xs text-grey-400 m-0">{getFileSizeInString(selectedFile.file)}</Paragraph>
        </Flex>
        <Button variant="clear" color="grey" onPress={handleRemoveFile} isDisabled={isSubmitting}>
          <Icon name={Icons.Close} color="grey" />
        </Button>
      </Flex>
    );
  };

  const isVisible = useMemo(() => {
    if (selectedFile === null) {
      return true;
    }

    if (selectedFile.error !== null) {
      return true;
    }

    return false;
  }, [selectedFile]);

  return (
    <Modal
      isOpen={isOpen}
      width={460}
      onClose={handleClose}
      classNames={{
        body: "p-4",
        footer: "px-4 py-4",
        header: "p-4 pb-0",
      }}
      headerContent={<ModalHeader />}
      footerContent={
        <ModalFooter onClose={handleClose} onSubmit={handleSubmit} isSubmitting={isSubmitting} canSubmit={canSubmit} />
      }
      zIndex={99}
    >
      <Form form={form}>
        <Activity mode={isVisible ? "visible" : "hidden"}>
          <DropZone
            onDrop={handleDrop}
            description={
              <Paragraph className="mb-0">
                Accepted file types: <b>{FILE_CONSTANTS.ACCEPTED_EXTENSIONS.join(", ")}</b> only. Maximum file size is{" "}
                <b>{FILE_CONSTANTS.MAX_SIZE_MB}MB</b>.
              </Paragraph>
            }
            allowedFileTypesOnSelect={FILE_CONSTANTS.ACCEPTED_TYPES}
            isDisabled={!isVisible}
          />
        </Activity>
        {renderSelectedFile()}

        <Division className="mt-3">
          <Form.Item name="Purchaser" rules={[{ required: true, message: "Purchaser is required" }]}>
            <Select size="small" options={purchaserOptions} isClearable={false} isLoading={isPurchasersLoading} />
          </Form.Item>
        </Division>

        <Division className="mt-3">
          <Paragraph className="mb-1 mt-0 form-item-label">Special Instructions (optional)</Paragraph>
          <Form.Item name="">
            <Textarea maxLength={100} showCharCount placeholder="If anything in this file needs explaining — an unusual layout, an abbreviation, rows that aren't real promotions — tell us here." minRows={4} />
          </Form.Item>
        </Division>

        <Paragraph className="mt-3 mb-0 text-secondary-600 bg-secondary-50 py-2 px-3 rounded-medium border-1 border-color-secondary-100">
          After completing the process, you&apos;ll get an email and check the portal you can find the promotions in DRAFT status, review, edit if needed then "Activate".
        </Paragraph>
      </Form>
    </Modal>
  );
};

const MENU_KEYS = {
  CREATE_PROGRAM: "create-program",
  BULK_UPLOAD: "bulk-upload",
} as const;

const NewPromotionModalArea = () => {
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isCreateProgramOpen, setIsCreateProgramOpen] = useState(false);
  const { hasAnyPermission } = useUserPermissions();

  const canBulkUpload = hasAnyPermission([WorkspacePermissions.TRADE_PROMOTIONS_ADD]);
  const canCreateProgram = hasAnyPermission([WorkspacePermissions.TRADE_PROMOTIONS_ADD_UI]);

  const menuItems = useMemo<MenuItemProps[]>(() => {
    const items: MenuItemProps[] = [];
    if (canCreateProgram) {
      items.push({ key: MENU_KEYS.CREATE_PROGRAM, label: "Create promotion" });
    }
    if (canBulkUpload) {
      items.push({ key: MENU_KEYS.BULK_UPLOAD, label: "Bulk upload" });
    }
    return items;
  }, [canBulkUpload, canCreateProgram]);

  const handleMenuSelect = useCallback((key: string) => {
    if (key === MENU_KEYS.CREATE_PROGRAM) {
      setIsCreateProgramOpen(true);
      return;
    }
    if (key === MENU_KEYS.BULK_UPLOAD) {
      setIsBulkUploadOpen(true);
    }
  }, []);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <Fragment>
      <NewPromotionUploadModal isOpen={isBulkUploadOpen} onClose={() => setIsBulkUploadOpen(false)} />
      <CreateProgramDrawer isOpen={isCreateProgramOpen} onClose={() => setIsCreateProgramOpen(false)} />
      <HasAnyPermission
        requiredPermissions={[WorkspacePermissions.TRADE_PROMOTIONS_ADD, WorkspacePermissions.TRADE_PROMOTIONS_ADD_UI]}
      >
        <Menu items={menuItems} onSelect={handleMenuSelect} placement="bottom end">
          <Button className="m-0" htmlTitle="New Promotion">
            <Icon name={Icons.Plus} slot="start" />
            New Promotion
            <Icon name={Icons.ChevronDown} slot="end" />
          </Button>
        </Menu>
      </HasAnyPermission>
    </Fragment>
  );
};

export { NewPromotionModalArea };
